<script lang="ts">
	import type { DateRange, PoiCategory } from '$lib/components/pois/types';
	import type { ClassValue } from 'svelte/elements';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import HotelDateRangePicker from '$lib/components/molecules/date/hotelDateRangePicker/HotelDateRangePicker.svelte';
	import { MAX_SELECTION_DAYS } from '$lib/components/pois/constants';
	import PoiCategories from '$lib/components/pois/filters/PoiCategories.svelte';
	import {
		areCategoriesWithMichelinPois,
		getSelectedPoiCategoryFilters,
		isOnlyMichelinSelection,
	} from '$lib/components/pois/list/utils';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import {
		trackOpenDatePickers,
		trackValidateDatePickers,
	} from '$lib/services/tracking/googleAnalytics/datepickers';
	import { getPoiCategoryToggleTrackingEvent } from '$lib/services/tracking/googleAnalytics/pois';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import { transformStringDateToCalendarDate } from '$lib/utils/date';
	import { formatTrackingValue } from '$lib/utils/formatTrackingValue';
	import { type CalendarDate } from '@internationalized/date';
	import { poiCategoryTrackerNames } from '../constants';
	import FilterButton from './FilterButton.svelte';

	interface Props {
		selectedPoiCategories: PoiCategory[];
		displayAllCategories?: boolean;
		singleCategoryMode?: boolean;
		withPartner?: boolean;
		displayFilterButton?: boolean;
		classes?: ClassValue;
		onPoiCategoriesChange?: (info: {
			poiCategory: PoiCategory;
			selected: boolean;
			singleCategoryMode: boolean;
			updatedSelectedCategories: PoiCategory[];
		}) => void;
		onClickFilterButton?: () => void;
		onShowFilters?: () => void;
		onClearDates?: () => void;
		onSelectDates?: (dateRange: DateRange) => void;
	}

	const {
		selectedPoiCategories,
		displayAllCategories = false,
		singleCategoryMode = false,
		withPartner = false,
		displayFilterButton = true,
		classes,
		onPoiCategoriesChange,
		onClickFilterButton,
		onShowFilters,
		onClearDates,
		onSelectDates,
	}: Props = $props();

	const searchParams = getSearchParamsStore();
	const mapLayoutContext = getMapLayoutContext();
	const { poiMarkerWithDetailsEnabled } = mapLayoutContext;
	const hasOnlyAccommodation
		= $derived(selectedPoiCategories.length === 1 && selectedPoiCategories[0] === 'ACCOMMODATION');

	function handleSelectedDates(datesRange: CalendarDate[]) {
		poiMarkerWithDetailsEnabled.set(true);
		const startDate = datesRange?.[0].toString() ?? undefined;
		const endDate = datesRange?.[1].toString() ?? undefined;

		if (!startDate || !endDate) {
			return;
		}

		onSelectDates?.({
			startDate,
			endDate,
		});

		$searchParams.filters = {
			...$searchParams.filters,
			ACCOMMODATION_AVAILABILITY_DATE_RANGE: { startDate, endDate },
		};

		trackValidateDatePickers({
			context: 'poilist',
			poiCategory: poiCategoryTrackerNames.ACCOMMODATION,
			searchParams: $searchParams,
		});
	}

	function formatPoiCategorySelected(poiCategories: PoiCategory[]) {
		const poiCategorySelected = poiCategories.map(
			Poicategory => poiCategoryTrackerNames[Poicategory],
		);
		return formatTrackingValue(poiCategorySelected);
	}

	function showFilters() {
		onClickFilterButton?.();
		onShowFilters?.();
		track({
			action: 'pois_advanced_filter',
			payload: {
				list_poi_category_selected: formatPoiCategorySelected(selectedPoiCategories),
			},
		});
	}

	const accommodationAvailabilityDateRange = $derived($searchParams?.filters
		?.ACCOMMODATION_AVAILABILITY_DATE_RANGE as DateRange);
	const numberOfFiltersForSelectedPoiCategories = $derived.by(() => {
		if (
			areCategoriesWithMichelinPois(selectedPoiCategories)
			&& isOnlyMichelinSelection($searchParams)
		) {
			return $searchParams.filters ? getSelectedPoiCategoryFilters($searchParams.filters, selectedPoiCategories).length : 0;
		} else if (selectedPoiCategories.length && $searchParams.filters && Object.keys($searchParams?.filters).length && $searchParams?.poiCategories) {
			return $searchParams.filters && getSelectedPoiCategoryFilters($searchParams.filters, selectedPoiCategories).length;
		}
		return 0;
	});

	function handleClickDates(inputId: string) {
		trackOpenDatePickers({
			context: 'poilist',
			inputId,
			poiCategory: poiCategoryTrackerNames.ACCOMMODATION,
			searchParams: $searchParams,
		});
	}
</script>

<div class={['mb-2 px-2', classes]}>
	<div
		class="flex justify-between flex-nowrap flex-row-reverse"
	>
		<div class="grow-3 order-2">
			<PoiCategories
				{selectedPoiCategories}
				{displayAllCategories}
				{singleCategoryMode}
				{withPartner}
				onChange={(categories, info) => {
					onPoiCategoriesChange?.({ ...info, updatedSelectedCategories: categories });
					track(getPoiCategoryToggleTrackingEvent({ ...info, updatedSelectedCategories: categories, context: 'poilist' }));
				}}
			/>
		</div>
		{#if displayFilterButton}
			<div class="ml-2 relative order-1">
				<FilterButton numberOfFilters={numberOfFiltersForSelectedPoiCategories} onclick={showFilters} />
			</div>
		{/if}
	</div>
	<div class="date-range-picker-container min-w-0 w-full order--1 mt-2">
		<div class="flex items-start gap-2">
			{#if hasOnlyAccommodation}
				<HotelDateRangePicker
					shouldStayOpenAfterClearing
					withNumberOfNights
					maxSelectionDays={MAX_SELECTION_DAYS}
					onOpen={handleClickDates}
					onSelectDates={handleSelectedDates}
					onClearDates={() => onClearDates?.()}
					initialValues={{
						start: transformStringDateToCalendarDate(
							accommodationAvailabilityDateRange?.startDate,
						),
						end: transformStringDateToCalendarDate(
							accommodationAvailabilityDateRange?.endDate,
						),
					}}
				/>
			{/if}
		</div>
	</div>
</div>
